import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HomeModernIcon, BuildingLibraryIcon } from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import { formatCurrency } from '../../utils/formatters';
import {
  calculateEquity,
  calculateLTV,
  generatePropertyMortgageProjection,
  getMortgagePaymentInfo,
} from '../../utils/mortgageCalculations';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid,
} from 'recharts';

const RealEstate = () => {
  const navigate = useNavigate();
  const { properties, mortgages } = useData();
  const [projectionYears, setProjectionYears] = useState(10);

  // Summary calculations
  const totalPropertyValue = properties.reduce((sum, p) => sum + (p.currentValue || 0), 0);
  const totalMortgageBalance = mortgages.reduce((sum, m) => sum + (m.currentBalance || 0), 0);
  const totalEquity = totalPropertyValue - totalMortgageBalance;
  const totalMonthlyPayments = mortgages.reduce((sum, m) => {
    const info = getMortgagePaymentInfo(m);
    return sum + info.monthlyPayment + (m.overpaymentMonthly || 0);
  }, 0);

  // Generate combined projection for all linked property-mortgage pairs
  const projectionMonths = projectionYears * 12;
  const combinedProjection = [];

  // Build a combined projection from all properties and mortgages
  if (properties.length > 0 || mortgages.length > 0) {
    for (let m = 0; m <= projectionMonths; m++) {
      let totalPropValue = 0;
      let totalMortBalance = 0;

      // Sum all property projections
      properties.forEach(property => {
        const mortgage = mortgages.find(mort => mort.id === property.linkedMortgageId);
        const data = generatePropertyMortgageProjection(property, mortgage, projectionMonths);
        if (data[m]) {
          totalPropValue += data[m].propertyValue;
          totalMortBalance += data[m].mortgageBalance;
        }
      });

      // Add unlinked mortgages
      mortgages.forEach(mortgage => {
        if (!properties.some(p => p.linkedMortgageId === mortgage.id)) {
          const data = generatePropertyMortgageProjection(null, mortgage, projectionMonths);
          if (data[m]) {
            totalMortBalance += data[m].mortgageBalance;
          }
        }
      });

      const monthDate = new Date();
      monthDate.setMonth(monthDate.getMonth() + m);
      combinedProjection.push({
        month: m,
        displayDate: new Intl.DateTimeFormat('en-GB', { month: 'short', year: '2-digit' }).format(monthDate),
        propertyValue: totalPropValue,
        mortgageBalance: totalMortBalance,
        equity: totalPropValue - totalMortBalance,
      });
    }
  }

  // Thin chart data
  const step = projectionYears <= 5 ? 1 : projectionYears <= 15 ? 3 : 6;
  const chartData = combinedProjection.filter((_, i) => i === 0 || i % step === 0 || i === combinedProjection.length - 1);

  const formatYAxis = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
    return value;
  };

  const hasData = properties.length > 0 || mortgages.length > 0;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold m-0">Real Estate Overview</h3>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/properties')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <HomeModernIcon className="w-4 h-4" />
            Properties ({properties.length})
          </button>
          <button
            onClick={() => navigate('/mortgages')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <BuildingLibraryIcon className="w-4 h-4" />
            Mortgages ({mortgages.length})
          </button>
        </div>
      </div>

      {!hasData ? (
        <div className="mt-6 flex flex-col items-center gap-4">
          <HomeModernIcon className="w-12 h-12 text-gray-300" />
          <p className="text-gray-600">No properties or mortgages added yet</p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/properties')}
              className="px-4 py-2 text-white rounded-lg font-medium transition-colors shadow-sm"
              style={{ backgroundColor: '#012E58' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
            >
              Add a Property
            </button>
            <button
              onClick={() => navigate('/mortgages')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Add a Mortgage
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Total Property Value</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(totalPropertyValue, 'GBP')}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Total Mortgage Balance</p>
              <p className="text-xl font-bold text-red-600 mt-1">{formatCurrency(totalMortgageBalance, 'GBP')}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Total Equity</p>
              <p className="text-xl font-bold text-green-600 mt-1">{formatCurrency(totalEquity, 'GBP')}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Monthly Payments</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(totalMonthlyPayments, 'GBP')}</p>
            </div>
          </div>

          {/* Combined Projection Chart */}
          {chartData.length > 1 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Total Property Value vs Mortgage Balance</h3>
                <div className="flex gap-2">
                  {[5, 10, 15, 25].map(years => (
                    <button
                      key={years}
                      onClick={() => setProjectionYears(years)}
                      className={`px-3 py-1 text-xs rounded-lg ${projectionYears === years ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      style={projectionYears === years ? { backgroundColor: '#012E58' } : {}}
                    >
                      {years}y
                    </button>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="displayDate" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value, name) => [formatCurrency(value, 'GBP'), name]} />
                  <Legend />
                  <Area type="monotone" dataKey="equity" fill="#bfdbfe" stroke="none" name="Equity" />
                  <Line type="monotone" dataKey="propertyValue" stroke="#059669" strokeWidth={2} dot={false} name="Property Value" />
                  <Line type="monotone" dataKey="mortgageBalance" stroke="#dc2626" strokeWidth={2} dot={false} name="Mortgage Balance" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Property & Mortgage Lists */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Properties */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Properties</h3>
                <button onClick={() => navigate('/properties')} className="text-xs text-blue-600 hover:underline">
                  View All →
                </button>
              </div>
              {properties.length === 0 ? (
                <p className="text-sm text-gray-500">No properties added</p>
              ) : (
                <div className="space-y-2">
                  {properties.map(property => {
                    const mortgage = mortgages.find(m => m.id === property.linkedMortgageId);
                    const equity = calculateEquity(property.currentValue, mortgage?.currentBalance || 0);
                    return (
                      <div
                        key={property.id}
                        onClick={() => navigate(`/properties/${property.id}`)}
                        className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{property.name}</p>
                            {property.address && <p className="text-xs text-gray-500">{property.address}</p>}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-gray-900">{formatCurrency(property.currentValue, property.currency)}</p>
                            <p className="text-xs text-green-600">Equity: {formatCurrency(equity, property.currency)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Mortgages */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Mortgages</h3>
                <button onClick={() => navigate('/mortgages')} className="text-xs text-blue-600 hover:underline">
                  View All →
                </button>
              </div>
              {mortgages.length === 0 ? (
                <p className="text-sm text-gray-500">No mortgages added</p>
              ) : (
                <div className="space-y-2">
                  {mortgages.map(mortgage => {
                    const info = getMortgagePaymentInfo(mortgage);
                    return (
                      <div
                        key={mortgage.id}
                        onClick={() => navigate(`/mortgages/${mortgage.id}`)}
                        className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{mortgage.name}</p>
                            <p className="text-xs text-gray-500">{mortgage.lender} - {info.currentRate}% {info.isInFixedPeriod ? '(Fixed)' : '(SVR)'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-red-600">{formatCurrency(mortgage.currentBalance, mortgage.currency)}</p>
                            <p className="text-xs text-gray-500">{formatCurrency(info.monthlyPayment, mortgage.currency)}/mo</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RealEstate;
